/**
 * Privacy masking — the "I am about to share my screen" switch.
 *
 * WHY (operator, 2026-09-08). Nothing in this app masked anything. Real
 * applicant names and email addresses, member and employee names, contract
 * values and the customer agency all rendered in plain text, on the screens
 * most likely to be projected in a demo or visible over a shoulder at a
 * customer site.
 *
 * WHAT THIS IS NOT. This is not an access control. Every value masked here was
 * already authorised for this viewer — RLS decided that. Masking is about who
 * is standing BEHIND the authorised viewer, which no server-side policy can
 * see. Treating it as security would be a mistake in the dangerous direction:
 * it is presentation, and it lives entirely in the browser.
 *
 * THE MASKS. Each one keeps the shape of the value so a layout does not jump
 * and a reader can still tell rows apart:
 *
 *   money    $4.2M            -> $•••      (magnitude is the sensitive part)
 *   person   Tejagni Chichili -> T. C.     (still distinguishable, not readable)
 *   email    t@jobhunt.com    -> t•••@•••  (domain hidden: it identifies too)
 *   text     Dept of Veterans -> •••••     (agency, contract number, free text)
 *   count    7                -> 7         (NOT masked; see below)
 *
 * COUNTS ARE DELIBERATELY NOT MASKED. "7 open seats" identifies nobody and
 * reveals no commercial term, and masking every number would make the app
 * unusable while sharing rather than merely discreet. The line is drawn at
 * VALUES (money) and IDENTITIES (people), which is what the operator asked
 * for: "proprietary company data and numbers… people".
 */

/** Masks money while keeping the currency legible. */
export function maskMoney(formatted: string): string {
  return formatted.replace(/[\d.,]+/g, '•••');
}

/**
 * "Tejagni Chichili" -> "T. C."
 *
 * Initials rather than a solid block so a recruiter scanning a list can still
 * tell one row from another and keep their place, which they cannot do if every
 * name is identical.
 */
export function maskPerson(name: string | null | undefined): string {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  return parts.map((p) => `${p[0].toUpperCase()}.`).join(' ');
}

/**
 * "tejagni@jobhuntmails.com" -> "t•••@•••"
 *
 * The domain is masked too: a personal address at a named employer identifies
 * someone as surely as their name does.
 */
export function maskEmail(email: string | null | undefined): string {
  if (!email) return '—';
  const at = email.indexOf('@');
  if (at <= 0) return '•••';
  return `${email[0]}•••@•••`;
}

/** Free text that is commercially sensitive: agency, contract number, title. */
export function maskText(value: string | null | undefined): string {
  if (!value) return '—';
  return '•••••';
}

/**
 * Format a number as USD. The single formatter this app should reach for.
 *
 * WHY THIS EXISTS. The privacy sweep found thirteen files that had each
 * hand-rolled their own `Intl.NumberFormat` currency helper — usually named
 * `fmt` — and every one of them rendered fully readable dollars while privacy
 * mode was on. They were invisible to any coverage check that greps for the
 * privacy hooks, so the app reported itself as covered while the contract
 * workspace and the client portal leaked. A local formatter is not a style
 * choice; it is the leak.
 *
 * `signed` prints an explicit + for positive values, for surfaces where the
 * DIRECTION of a change is the point (a funding action, a modification). The
 * sign stays outside the mask: that a mod added value is not the sensitive
 * part, the figure is.
 *
 * Callers pass the mask (`usePrivacy().money`) rather than this module reading
 * it, because privacy lives in React context and this file is hook-free by
 * design — which is what lets module-level helpers use it.
 */
export function formatCurrency(
  value: number | null | undefined,
  mask: (formatted: string) => string,
  opts: { decimals?: number; signed?: boolean } = {},
): string {
  if (value === null || value === undefined) return '—';
  const { decimals = 0, signed = false } = opts;
  const magnitude = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(signed ? Math.abs(value) : value);
  if (!signed) return mask(magnitude);
  return `${value < 0 ? '-' : '+'}${mask(magnitude)}`;
}
